import {useEffect, useRef} from 'react';

// The region is cleared at once and the message written after this delay, so the change registers
const WRITE_DELAY_MS = 100;

/**
 * Custom hook to announce state changes to screen readers.
 * Uses one visually hidden live region, kept from mount to unmount: screen readers often miss a
 * region inserted along with its content, so only its text and priority change between messages.
 *
 * @param message - The message to announce; an empty message announces nothing
 * @param priority - 'polite' (default) or 'assertive' for urgent messages
 */
export const useScreenReaderAnnouncement = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const regionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const region = document.createElement('div');
        region.setAttribute('aria-atomic', 'true');
        region.className = 'sr-only';
        document.body.appendChild(region);
        regionRef.current = region;

        return () => {
            region.remove();
            regionRef.current = null;
        };
    }, []);

    useEffect(() => {
        const region = regionRef.current;
        if (!region) {
            return;
        }

        region.setAttribute('aria-live', priority);
        region.textContent = '';
        if (!message) {
            return;
        }

        const timer = setTimeout(() => {
            region.textContent = message;
        }, WRITE_DELAY_MS);

        // A message replaced within the delay is never written
        return () => clearTimeout(timer);
    }, [message, priority]);
};
